"use client";

import Card from "@/components/common/Card";
import LogoImage from "@/components/common/LogoImage";
import ProcessSection from "@/components/request/ProcessSection";
import SubmitForm from "@/components/request/renewal/SubmitForm";
import { EClientKey } from "@/constants/clients";

function RequestLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section with Two Cards */}
        <section className="relative px-side pb-16 pt-12">
          <div className="mx-auto max-w-[1280px]">
            {/* Centered Title */}
            <div className="mb-12 text-center">
              <h1 className="tablet:text-32 mb-1 text-26 font-bold text-slate-900 desktop:text-48">
                채용 공고에 나타나지 않는 인재,
                <br className="desktop:hidden" /> <span className="text-blue-600">AI가 직접 찾아드립니다.</span>
              </h1>
              <p className="mx-auto max-w-3xl text-14 leading-relaxed text-slate-600 tablet:text-16 desktop:text-18">
                역량 메타데이터 기반 AI 소싱으로
                <br className="desktop:hidden" /> 포지션에 딱 맞는 후보자를 발굴합니다.
              </p>
            </div>

            {/* Two Cards Grid - Asymmetric Layout */}
            <div className="grid grid-cols-1 gap-6 desktop:grid-cols-12 desktop:gap-8">
              {/* Left Card - Service Introduction (5 columns) */}
              <Card className="h-full rounded-2xl border-slate-200 bg-white p-6 shadow-lg desktop:col-span-5 desktop:p-8">
                <div className="flex h-full flex-col">
                  {/* Main Headline */}
                  <div className="mb-6">
                    <h2 className="text-24 font-bold leading-tight text-slate-900 desktop:text-28">
                      우리 팀에 필요한
                      <br />
                      <span className="bg-gradient-to-r from-sky-600 to-blue-700 bg-clip-text text-transparent">핵심 인재</span>
                      를
                      <br />
                      리스트로 바로 만나보세요
                    </h2>
                    <p className="desktop:text-15 mt-3 text-14 leading-relaxed text-slate-600">
                      AI가 외부 데이터를 증강 분석하여
                      <br />
                      숨은 인재의 역량과 적합 사유까지 알려드립니다.
                    </p>
                  </div>

                  {/* Value Propositions */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100">
                        <svg className="h-4 w-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-15 font-semibold text-slate-900">역량 메타데이터 분석</p>
                        <p className="text-13 text-slate-600">
                          외부 데이터 증강으로 이력서만으론 보이지 않는 역량까지 파악합니다
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100">
                        <svg className="h-4 w-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-15 font-semibold text-slate-900">빠른 소싱</p>
                        <p className="text-13 text-slate-600">며칠 걸리던 인재 검색, AI로 몇 시간이면 충분합니다</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100">
                        <svg className="h-4 w-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-15 font-semibold text-slate-900">맞춤 인사이트 리포트</p>
                        <p className="text-13 text-slate-600">후보자별 적합 사유, 추천 순위, 개인화 메시지까지 제공합니다</p>
                      </div>
                    </div>
                  </div>

                  {/* Client Logos with Auto Slide - Two Rows */}
                  <div className="mt-auto pt-8">
                    <div className="border-t border-slate-100 pt-6">
                      <p className="text-13 mb-4 font-medium text-slate-700">함께하는 고객사</p>
                      <div className="space-y-3">
                        {/* First Row */}
                        <div className="relative overflow-hidden">
                          <div className="animate-slide flex gap-5">
                            {/* First set of logos */}
                            <div className="flex shrink-0 items-center gap-5">
                              <LogoImage client={EClientKey.GS_RETAIL} className="h-8" />
                              <LogoImage client={EClientKey.OLIVE_YOUNG} className="h-8" />
                              <LogoImage client={EClientKey.GANGNAM_UNNI} className="h-8" />
                              <LogoImage client={EClientKey.LEMON_BASE} className="h-8" />
                              <LogoImage client={EClientKey.RIIID} className="h-8" />
                              <LogoImage client={EClientKey.COINONE} className="h-8" />
                            </div>
                            {/* Duplicate set for seamless loop */}
                            <div className="flex shrink-0 items-center gap-5">
                              <LogoImage client={EClientKey.GS_RETAIL} className="h-8" />
                              <LogoImage client={EClientKey.OLIVE_YOUNG} className="h-8" />
                              <LogoImage client={EClientKey.GANGNAM_UNNI} className="h-8" />
                              <LogoImage client={EClientKey.LEMON_BASE} className="h-8" />
                              <LogoImage client={EClientKey.RIIID} className="h-8" />
                              <LogoImage client={EClientKey.COINONE} className="h-8" />
                            </div>
                          </div>
                        </div>
                        {/* Second Row */}
                        <div className="relative overflow-hidden">
                          <div className="animate-slide-reverse flex gap-5">
                            {/* First set of logos */}
                            <div className="flex shrink-0 items-center gap-5">
                              <LogoImage client={EClientKey.KREAM} className="h-10" />
                              <LogoImage client={EClientKey.WISELY} className="h-10" />
                              <LogoImage client={EClientKey.MODUSIGN} className="h-10" />
                              <LogoImage client={EClientKey.VNTG} className="h-10" />
                              <LogoImage client={EClientKey.EST_AID} className="h-10" />
                              <LogoImage client={EClientKey.HEUM} className="h-10" />
                            </div>
                            {/* Duplicate set for seamless loop */}
                            <div className="flex shrink-0 items-center gap-5">
                              <LogoImage client={EClientKey.KREAM} className="h-10" />
                              <LogoImage client={EClientKey.WISELY} className="h-10" />
                              <LogoImage client={EClientKey.MODUSIGN} className="h-10" />
                              <LogoImage client={EClientKey.VNTG} className="h-10" />
                              <LogoImage client={EClientKey.EST_AID} className="h-10" />
                              <LogoImage client={EClientKey.HEUM} className="h-10" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Right Card - Form (7 columns) */}
              <div className="h-full desktop:col-span-7">
                <SubmitForm />
              </div>
            </div>
          </div>
        </section>

        {/* Service Process Section */}
        <section className="border-y border-slate-100 bg-white px-side py-16">
          <div className="mx-auto max-w-[1280px]">
            <ProcessSection />
          </div>
        </section>
      </div>

      {children}
    </>
  );
}

export default RequestLayout;
